import { ReactNode, useMemo } from 'react';
import { toast } from 'sonner';
import { SectionRequirementsData } from '@/api/ai';
import { SubscriptionPlansModal } from '@/components/modals/SubscriptionPlansModal';
import { useAiGenerationLock } from '@/hooks/use-ai-generation-lock';
import { useClinicalHistoryStatus } from '@/hooks/use-clinical-history-status';
import { useDocumentSourcePresence } from '@/hooks/use-document-source-presence';
import { useFeatureEntitlement } from '@/hooks/use-feature-entitlement';
import { useModal } from '@/hooks/use-modal';
import { toReportStepKey } from '@/lib/analytics-events';
import { getEntitlementBlockedMessage } from '@/lib/entitlements';
import { capture } from '@/lib/posthog';
import { useReportWizard } from '@/pages/create-report/context/ReportWizardContext';
import { ReportData } from '@/types/report';
import {
    getFirstBlockingSource,
    getResolvedSectionSources,
    getResolvedRequiredSourcesStatus,
    getSourceRowId,
} from './ai-data-sources';
import AICardShell from './AICardShell';
import AIDataSources, { SourceActionsMap } from './AIDataSources';
import AIGenerateButton from './AIGenerateButton';
import { GateAction, GateCount } from './AIPanelGate';
import { SECTIONS } from './constants';
import { Sections } from './types';

interface AIGeneratorCardProps {
    onGenerate: () => void;
    children?: ReactNode;
    sourceStepId?: Sections;
    reportData?: ReportData;
    sourceActions?: SourceActionsMap;
    backendRequirements?: SectionRequirementsData;
}

export default function AIGeneratorCard({
    onGenerate,
    children,
    sourceStepId,
    reportData,
    sourceActions,
    backendRequirements,
}: AIGeneratorCardProps) {
    // Read from the wizard rather than taking a prop: every call site passed the
    // same global flag, which said nothing about *this* card and left it showing
    // a spinner for a sibling section's work.
    const { isGeneratingThis, blockedByOtherReason } = useAiGenerationLock(sourceStepId);
    const { generationPhase, cancelGeneration } = useReportWizard();
    const aiCreditAccess = useFeatureEntitlement('ai.credits');
    const { openModal, closeModal } = useModal();
    const shouldTrackClinicalHistoryStatus =
        sourceStepId === SECTIONS.DEMAND_DESCRIPTION ||
        sourceStepId === SECTIONS.CLINICAL_HISTORY ||
        sourceStepId === SECTIONS.DIAGNOSIS ||
        sourceStepId === SECTIONS.RECOMMENDATIONS;

    const { data: clinicalHistoryResponse } = useClinicalHistoryStatus(reportData?.id, {
        enabled: Boolean(shouldTrackClinicalHistoryStatus && reportData?.id),
    });

    const clinicalHistoryStatus = clinicalHistoryResponse?.data.clinical_history.status;
    const clinicalHistoryReady = clinicalHistoryResponse?.data.clinical_history.ready;

    // `ReportWizardContext` hands every consumer a fresh ~70-key object literal,
    // so this panel re-renders on each keystroke in the document. Memoising the
    // chain below is what lets `mergedActions` — and therefore `AIDataSources` —
    // keep their identity across those renders.
    const sourceContext = useMemo(
        () => ({
            stepId: sourceStepId,
            clinicalHistoryStatus,
            clinicalHistoryReady,
        }),
        [sourceStepId, clinicalHistoryStatus, clinicalHistoryReady],
    );

    const resolvedSources = useMemo(
        () =>
            sourceStepId && reportData
                ? getResolvedSectionSources(
                      sourceStepId,
                      backendRequirements,
                      reportData,
                      sourceContext,
                  )
                : [],
        [sourceStepId, backendRequirements, reportData, sourceContext],
    );

    const describedSources = useDocumentSourcePresence(resolvedSources, reportData?.patient?.id);

    const requiredSourcesStatus = useMemo(
        () => getResolvedRequiredSourcesStatus(describedSources),
        [describedSources],
    );

    const isBlockedBySources =
        requiredSourcesStatus.hasMissingRequired ||
        requiredSourcesStatus.hasProcessingRequired ||
        requiredSourcesStatus.hasFailedRequired;

    // Not disabled while this card's own run is in flight any more: the button
    // is the stop, so the one moment it used to be dead is the one moment it
    // has the most to do.
    const isGenerateDisabled =
        !isGeneratingThis &&
        (Boolean(blockedByOtherReason) || aiCreditAccess.shouldBlock || isBlockedBySources);

    // The lock's sentence now has a visible home in the status line below the
    // button, so the tooltip that used to carry it would be the same words in
    // two places. What the line cannot hold is the way out — a tooltip has no
    // room for a control — so the blocked click answers with it instead.
    const isBlockedByOtherGeneration =
        !isGeneratingThis &&
        Boolean(blockedByOtherReason) &&
        !aiCreditAccess.shouldBlock &&
        !isBlockedBySources;
    const statusLineId = sourceStepId ? `ai-generate-status-${sourceStepId}` : undefined;

    // The disabled CTA says nothing on its own — it points at the topmost row
    // that is holding it back, which already names the actor and the route.
    const blockingSource = useMemo(
        () => (isBlockedBySources ? getFirstBlockingSource(describedSources) : undefined),
        [isBlockedBySources, describedSources],
    );
    const blockingRowId =
        blockingSource && sourceStepId
            ? getSourceRowId(sourceStepId, blockingSource.id)
            : undefined;

    // Nothing is said while the generation runs: the button reads "Interromper"
    // and the laudo is already animating the section being written, so a line
    // repeating it would only be one more thing on screen. What is left are the
    // two things neither of those can say — why a stop is taking a moment, and
    // why this card cannot run at all.
    const statusLine = isGeneratingThis
        ? generationPhase === 'cancelling'
            ? 'Isso leva alguns segundos.'
            : ''
        : (blockedByOtherReason ?? '');

    const creditsMessage = getEntitlementBlockedMessage(aiCreditAccess.decision);
    const creditsPlan =
        aiCreditAccess.isUnavailableInPlan && aiCreditAccess.availableFromPlan
            ? aiCreditAccess.availableFromPlan
            : null;
    const creditsReasonId = sourceStepId ? `ai-credits-blocked-${sourceStepId}` : undefined;

    // The message already says what happened and which plan lifts it, so the
    // modal is opened without repeating it in a description.
    const handleOpenPlans = () => {
        openModal({
            title: 'Escolher um plano',
            content: <SubscriptionPlansModal close={closeModal} description={creditsMessage} />,
            size: 'xl',
        });
    };

    const handleGenerate = () => {
        if (!onGenerate || isGenerateDisabled || isGeneratingThis) return;

        // One choke point for all four section cards. The API owns
        // `ai_generation_used`, so the gap between that and this is generations
        // that failed, were cancelled, or ran out of credits.
        const stepKey = toReportStepKey(sourceStepId, false);
        if (stepKey) {
            capture('report_ai_generation_requested', {
                step_key: stepKey,
                surface: 'section_card',
                mode: 'create',
            });
        }

        onGenerate();
    };

    return (
        <AICardShell ready={!isBlockedBySources && !aiCreditAccess.shouldBlock}>
            {/* Running out of credits is a blocker like any other, so it is
                written like one: the sentence, then the route. The Alert box,
                the plan chip and the dismissible 80% notice that used to live
                here were three different shapes for the same idea. */}
            {aiCreditAccess.shouldBlock && (
                <div className="flex flex-col gap-2.5">
                    <GateCount count={1} />
                    <p id={creditsReasonId} className="text-[13px] leading-snug text-foreground">
                        {creditsMessage}
                    </p>
                    <GateAction
                        label={creditsPlan ? `Ver o plano ${creditsPlan.name}` : 'Ver planos'}
                        onClick={handleOpenPlans}
                        describedBy={creditsReasonId}
                    />
                </div>
            )}

            {sourceStepId && reportData && (
                <AIDataSources
                    sources={describedSources}
                    stepId={sourceStepId}
                    actions={sourceActions ?? {}}
                />
            )}

            {children}

            <AIGenerateButton
                onClick={handleGenerate}
                onCancel={() => void cancelGeneration()}
                disabled={isGenerateDisabled}
                isGenerating={isGeneratingThis}
                phase={generationPhase}
                // Only points at the status line when the line is actually
                // rendered — a dangling `aria-describedby` is read as nothing
                // at all, which is worse than no description.
                describedBy={blockingRowId ?? (statusLine ? statusLineId : undefined)}
                // A greyed CTA invites a click before it invites a hover, and
                // this is the one blocker the person can clear right now — so
                // the click gets the route rather than a repeat of the line
                // already on screen.
                onBlockedClick={
                    isBlockedByOtherGeneration && blockedByOtherReason
                        ? () =>
                              toast.warning(blockedByOtherReason, {
                                  action: {
                                      label: 'Interromper',
                                      onClick: () => void cancelGeneration(),
                                  },
                              })
                        : undefined
                }
                className="w-full gap-2 rounded-xl py-6 text-white"
            />

            {/* Rendered only when there is something to say. It used to hold
                the running label and so kept its space reserved; with that gone
                it was an empty node in a `gap-4` column, which is 16 px of
                separation between the button and the link below it in the state
                the panel spends almost all its time in. The shift it now causes
                lands on a card that is already changing — the CTA greys, or the
                stop starts spinning — and never under a cursor mid-task. */}
            {statusLine ? (
                <p
                    id={statusLineId}
                    role="status"
                    className="text-center text-[11px] leading-4 text-muted-foreground"
                >
                    {statusLine}
                </p>
            ) : null}
        </AICardShell>
    );
}
